import WorkflowRegistryError from './workflow-registry-error'

const createRegistrationKey = (type, version) => `${type}@${version}`

const isStepImplementation = implementation => (
    implementation
    && implementation.descriptor
    && typeof implementation.descriptor.type === 'string'
    && implementation.descriptor.type.length > 0
    && Number.isInteger(implementation.descriptor.version)
    && implementation.descriptor.version > 0
    && implementation.component
    && typeof implementation.createController === 'function'
    && (
        typeof implementation.migrateSettings === 'undefined'
        || typeof implementation.migrateSettings === 'function'
    )
    && (
        typeof implementation.createReviewContribution === 'undefined'
        || typeof implementation.createReviewContribution === 'function'
    )
)

const createStepRegistry = () => {
    const registrations = new Map()

    const get = (type, version) => registrations.get(createRegistrationKey(type, version)) || null

    const resolve = (type, version) => {
        const registration = get(type, version)

        if (!registration) {
            throw new WorkflowRegistryError(
                'UNKNOWN_STEP_TYPE_VERSION',
                `Step type "${type}" version ${version} is not registered`,
                { type, version },
            )
        }

        return registration
    }

    return Object.freeze({
        register(implementation) {
            if (!isStepImplementation(implementation)) {
                throw new WorkflowRegistryError(
                    'INVALID_STEP_REGISTRATION',
                    'A step registration requires a descriptor, component, and createController()',
                )
            }

            const { type, version } = implementation.descriptor
            const key = createRegistrationKey(type, version)

            if (registrations.has(key)) {
                throw new WorkflowRegistryError(
                    'DUPLICATE_STEP_TYPE_VERSION',
                    `Step type "${type}" version ${version} is already registered`,
                    { type, version },
                )
            }

            const registration = Object.freeze({ ...implementation })

            registrations.set(key, registration)

            return registration
        },
        get,
        resolve,
        has(type, version) {
            return registrations.has(createRegistrationKey(type, version))
        },
        hasType(type) {
            return Array.from(registrations.values())
                .some(registration => registration.descriptor.type === type)
        },
        versionsFor(type) {
            return Array.from(registrations.values())
                .filter(registration => registration.descriptor.type === type)
                .map(registration => registration.descriptor.version)
                .sort((left, right) => left - right)
        },
    })
}

export { createStepRegistry }
export default createStepRegistry
